import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Loader2 } from "lucide-react";
import remarkGfm from "remark-gfm";
import { MARKDOWN_COMPONENTS } from "~/components/assistant/lib/markdownComponents";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";

interface DocumentModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  markdownPath: string;
}

export function DocumentModal({ open, onClose, title, markdownPath }: DocumentModalProps) {
  const [content, setContent] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setError(null);

      fetch(markdownPath)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load document: ${response.statusText}`);
          }
          return response.text();
        })
        .then((text) => {
          setContent(text);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error loading markdown:", err);
          setError("Failed to load document");
          setIsLoading(false);
        });
    }
  }, [open, markdownPath]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="w-[80vw] sm:max-w-[1024px] h-[90vh] flex flex-col p-0 gap-0"
        aria-describedby={undefined}
      >
        <DialogHeader className="px-6 pt-4 pb-4 border-b shrink-0">
          <DialogTitle className="text-sm">{title}</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto px-6 py-4 flex-1 text-sm">
          {isLoading && (
            <div className="flex flex-col gap-2 w-full h-full justify-center items-center">
              <Loader2 className="size-8 text-gray-400 animate-spin" />
            </div>
          )}

          {error && <div className="text-red-600 text-center py-8">{error}</div>}

          {!isLoading && !error && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                ...MARKDOWN_COMPONENTS,
                img: ({ node, ...props }) => {
                  // Convert relative image paths to absolute paths from the public folder. This is important.
                  const src = props.src?.startsWith("assets/")
                    ? `/assets/docs/dataset-agent/${props.src}`
                    : props.src;
                  return (
                    <img {...props} src={src} alt={props.alt || ""} className="rounded-lg my-4" />
                  );
                },
              }}
            >
              {content}
            </ReactMarkdown>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
